import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import { logAdminAction } from '../../utils/auditLogger';
import { Dialog } from '@headlessui/react';

export default function CustomerDetails() {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [customer, setCustomer] = useState({
    name: '',
    customerId: '',
    status: '',
    address: {
      zipCode: '',
      city: '',
      fullAddress: '',
      country: '',
      countryCode: ''
    },
    project: {
      id: '',
      name: '',
      status: ''
    },
    primaryContact: {
      lastName: '',
      firstName: '',
      email: '',
      phone: '',
      position: '',
      newsletter: false
    },
    secondaryContact: {
      lastName: '',
      firstName: '',
      email: '',
      phone: '',
      position: '',
      newsletter: false
    },
    taxDetails: {
      taxNumber: '',
      euTaxNumber: '',
      registrationNumber: '',
      groupTaxNumber: '',
      thirdCountryTaxId: ''
    },
    payment: {
      defaultPaymentMethod: 'átutalás',
      defaultDueDate: 0
    },
    notes: {
      invoiceNote: '',
      internalNote: ''
    }
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const docRef = doc(db, 'customers', customerId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const customerData = {
            name: '',
            customerId: '',
            status: '',
            address: {
              zipCode: '',
              city: '',
              fullAddress: '',
              country: '',
              countryCode: ''
            },
            project: {
              id: '',
              name: '',
              status: ''
            },
            primaryContact: {
              lastName: '',
              firstName: '',
              email: '',
              phone: '',
              position: '',
              newsletter: false
            },
            secondaryContact: {
              lastName: '',
              firstName: '',
              email: '',
              phone: '',
              position: '',
              newsletter: false
            },
            taxDetails: {
              taxNumber: '',
              euTaxNumber: '',
              registrationNumber: '',
              groupTaxNumber: '',
              thirdCountryTaxId: ''
            },
            payment: {
              defaultPaymentMethod: 'átutalás',
              defaultDueDate: 0
            },
            notes: {
              invoiceNote: '',
              internalNote: ''
            },
            ...docSnap.data()
          };
          
          console.log('Loaded customer data:', customerData);
          setCustomer(customerData);
        } else {
          toast.error('Az ügyfél nem található');
          navigate('/admin/customers');
        }
      } catch (error) {
        console.error('Error fetching customer:', error);
        toast.error('Hiba történt az ügyfél betöltésekor');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId, navigate]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        console.log('Fetching projects...');
        const projectsSet = new Set();
        const customersRef = collection(db, 'customers');
        const querySnapshot = await getDocs(customersRef);
        
        console.log('Total documents:', querySnapshot.size);
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('Document data:', data);
          
          if (data.project?.name && data.project?.id) {
            const projectData = {
              name: data.project.name,
              id: data.project.id,
              status: data.project.status || ''
            };
            console.log('Adding project:', projectData);
            projectsSet.add(JSON.stringify(projectData));
          }
        });

        const uniqueProjects = Array.from(projectsSet)
          .map(p => {
            try {
              return JSON.parse(p);
            } catch (e) {
              console.error('Error parsing project:', e, p);
              return null;
            }
          })
          .filter(p => p !== null)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        console.log('Unique projects:', uniqueProjects);
        setAvailableProjects(uniqueProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const docRef = doc(db, 'customers', customerId);
      await updateDoc(docRef, {
        ...customer,
        updatedAt: new Date()
      });
      
      toast.success('Ügyfél sikeresen módosítva');
      navigate(`/admin/customers/${customerId}`);
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Hiba történt a mentés során');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (section, field, value) => {
    if (section) {
      setCustomer(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setCustomer(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleBack = () => {
    navigate('/admin/customers');
  };

  const handleCancel = () => {
    navigate('/admin/customers');
  };

  const handleProjectSelect = (selectedProject) => {
    if (!selectedProject) {
      setCustomer(prev => ({
        ...prev,
        project: { name: '', id: '', status: '' }
      }));
      return;
    }

    const [projectName, projectId] = selectedProject.split('|');
    const existingProject = availableProjects.find(p => p.id === projectId);

    setCustomer(prev => ({
      ...prev,
      project: {
        name: projectName,
        id: projectId,
        status: existingProject?.status || prev.project.status || ''
      }
    }));
  };

  // Jelszó-emlékeztető küldése
  const handleSendPasswordReset = async () => {
    if (!customer.primaryContact?.email) {
      toast.error('Az ügyfélnek nincs e-mail címe');
      return;
    }

    setSendingPasswordReset(true);

    try {
      // Cloud Function hívása a jelszó-visszaállító e-mail küldéséhez
      const response = await fetch('https://us-central1-elonyklub.cloudfunctions.net/sendPasswordResetEmails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emails: [customer.primaryContact.email]
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Hiba történt a jelszó-visszaállító e-mail küldésekor');
      }

      // Eredmények feldolgozása
      const result = data.results.find(r => r.email === customer.primaryContact.email);
      
      if (result && result.success) {
        // Értesítés rögzítése az adatbázisban
        await addDoc(collection(db, `customers/${customerId}/notifications`), {
          type: 'password_reset',
          sentAt: serverTimestamp(),
          email: customer.primaryContact.email,
          sentBy: 'admin'
        });
        
        // Audit log
        await logAdminAction('customer.password_reset', {
          customerId: customerId,
          email: customer.primaryContact.email
        });

        toast.success('Jelszó-visszaállítási e-mail sikeresen elküldve');
      } else {
        toast.error('Hiba történt a jelszó-visszaállítási e-mail küldésekor');
      }
      
      setIsPasswordResetModalOpen(false);
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error('Hiba történt a jelszó-visszaállítási e-mail küldésekor');
    } finally {
      setSendingPasswordReset(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Icons.Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-600 shadow-xl relative overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back button bar */}
          <div className="py-4">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/25 hover:bg-white/35 rounded-lg text-white font-medium transition-all duration-200 backdrop-blur-sm shadow-md text-base"
            >
              <Icons.ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
              <span>{t('common.back')}</span>
            </button>
          </div>
          
          {/* Main header content */}
          <div className="py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              {/* Left side - Title and customer info */}
              <div className="flex items-start gap-5">
                <div className="h-16 w-16 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/10">
                  <span className="text-2xl font-bold text-white">
                    {customer?.name?.charAt(0).toUpperCase() || '?'}
                  </span>
                </div>
                <div className="space-y-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-white">
                    {customer?.name || t('customers.unknownCustomer')}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4">
                    {customer?.primaryContact?.email && (
                      <a href={`mailto:${customer.primaryContact.email}`} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors group">
                        <div className="p-1.5 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors">
                          <Icons.Mail className="h-4 w-4" />
                        </div>
                        <span>{customer.primaryContact.email}</span>
                      </a>
                    )}
                    {customer?.primaryContact?.phone && (
                      <a href={`tel:${customer.primaryContact.phone}`} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors group">
                        <div className="p-1.5 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors">
                          <Icons.Phone className="h-4 w-4" />
                        </div>
                        <span>{customer.primaryContact.phone}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Right side - Actions */}
              <div className="flex flex-wrap items-center gap-3 mt-4 md:mt-0">
                {/* Jelszó-emlékeztető küldése gomb */}
                {customer?.primaryContact?.email && (
                  <button
                    type="button"
                    onClick={() => setIsPasswordResetModalOpen(true)}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors inline-flex items-center gap-2 shadow-md"
                  >
                    <Icons.KeyRound className="h-4 w-4" />
                    Jelszó-emlékeztető küldése
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-white/15 hover:bg-white/25 rounded-lg backdrop-blur-sm transition-colors shadow-md"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  form="customer-form"
                  disabled={saving}
                  className="px-4 py-2.5 text-sm font-medium text-indigo-700 bg-white rounded-lg shadow-md hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 disabled:opacity-50 min-w-[100px] flex items-center justify-center transition-all duration-200"
                >
                  {saving ? (
                    <Icons.Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    t('common.save')
                  )}
                </button>
              </div>
            </div>

            {/* Status badges */}
            <div className="mt-6 flex flex-wrap items-center gap-3 pb-4">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm shadow-sm">
                <Icons.Circle className={`h-2 w-2 fill-current ${
                  customer?.status === 'Aktív' ? 'text-green-400' :
                  customer?.status === 'Inaktív' ? 'text-red-400' :
                  customer?.status === 'Új partner' ? 'text-blue-400' :
                  customer?.status === 'Pro ügyfél' ? 'text-purple-400' :
                  'text-gray-400'
                }`} />
                {customer?.status || t('customers.status.unknown')}
              </div>
              {customer?.referralCode && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm shadow-sm">
                  <Icons.Ticket className="h-3 w-3" />
                  <span>Ajánlói kód:</span>
                  <span className="font-mono font-bold">{customer.referralCode}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(customer.referralCode);
                      toast.success('Ajánlói kód másolva!');
                    }}
                    className="ml-1 text-white/70 hover:text-white"
                  >
                    <Icons.Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
              {customer?.inviteCode && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm shadow-sm">
                  <Icons.Users className="h-3 w-3" />
                  {t('customers.referral')}
                </div>
              )}
              {customer?.createdAt && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm shadow-sm">
                  <Icons.Calendar className="h-3 w-3" />
                  <span>Regisztrált:</span>
                  <time dateTime={customer.createdAt.toDate().toISOString()}>
                    {customer.createdAt.toDate().toLocaleDateString()}
                  </time>
                </div>
              )}
              {customer?.address?.city && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm shadow-sm">
                  <Icons.MapPin className="h-3 w-3" />
                  {customer.address.city}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Dekoratív elemek */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-blue-500 opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-indigo-300 opacity-10 blur-3xl"></div>
      </div>

      {/* Password Reset Confirmation Modal */}
      <Dialog
        open={isPasswordResetModalOpen}
        onClose={() => !sendingPasswordReset && setIsPasswordResetModalOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <Dialog.Title className="text-lg font-medium">
                Jelszó-emlékeztető küldése
              </Dialog.Title>
              <button
                onClick={() => !sendingPasswordReset && setIsPasswordResetModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
                disabled={sendingPasswordReset}
              >
                <Icons.X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-amber-100 rounded-full">
                  <Icons.KeyRound className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 text-center">
                  Jelszó-emlékeztető küldése
                </h3>
                <p className="mt-2 text-sm text-gray-500 text-center">
                  Biztosan küldeni szeretne jelszó-emlékeztetőt ennek az ügyfélnek?
                </p>
              </div>

              <div className="mt-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <Icons.User className="h-5 w-5 text-gray-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{customer?.name}</p>
                      <p className="text-sm text-gray-600">{customer?.primaryContact?.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsPasswordResetModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={sendingPasswordReset}
                >
                  Mégse
                </button>
                <button
                  type="button"
                  onClick={handleSendPasswordReset}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-lg hover:bg-amber-700 flex items-center"
                  disabled={sendingPasswordReset}
                >
                  {sendingPasswordReset ? (
                    <>
                      <Icons.Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Küldés folyamatban...
                    </>
                  ) : (
                    <>
                      <Icons.Send className="h-4 w-4 mr-2" />
                      Jelszó-emlékeztető küldése
                    </>
                  )}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Main Content */}
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <form id="customer-form" onSubmit={handleSubmit} className="space-y-6 max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Alapadatok</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Név</label>
                      <input
                        type="text"
                        value={customer.name}
                        onChange={(e) => handleChange(null, 'name', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Azonosító</label>
                      <input
                        type="text"
                        value={customer.customerId}
                        onChange={(e) => handleChange(null, 'customerId', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Státusz</label>
                      <select
                        value={customer.status}
                        onChange={(e) => handleChange(null, 'status', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="Új partner">Új partner</option>
                        <option value="Pro ügyfél">Pro ügyfél</option>
                        <option value="Régi ügyfél">Régi ügyfél</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ajánlói kód</label>
                      <div className="mt-1 flex">
                        <input
                          type="text"
                          value={customer.referralCode || ''}
                          readOnly
                          className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                        {customer.referralCode && (
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(customer.referralCode);
                              toast.success('Ajánlói kód másolva!');
                            }}
                            className="ml-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <Icons.Copy className="h-3 w-3 mr-1" />
                            Másolás
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Az ajánlói kódot a felhasználók automatikusan kapják regisztrációkor. Ez a kód nem módosítható.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Information */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Projekt adatok</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Projekt kiválasztása</label>
                      <select
                        value={customer.project?.name && customer.project?.id ? `${customer.project.name}|${customer.project.id}` : ''}
                        onChange={(e) => handleProjectSelect(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Válassz projektet...</option>
                        {availableProjects.map((project) => (
                          <option 
                            key={project.id} 
                            value={`${project.name}|${project.id}`}
                          >
                            {project.name} (ID: {project.id})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Projekt neve</label>
                      <input
                        type="text"
                        value={customer.project?.name || ''}
                        onChange={(e) => handleChange('project', 'name', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Projekt ID</label>
                      <input
                        type="text"
                        value={customer.project?.id || ''}
                        onChange={(e) => handleChange('project', 'id', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Projekt státusz</label>
                      <select
                        value={customer.project?.status || ''}
                        onChange={(e) => handleChange('project', 'status', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Válassz státuszt...</option>
                        <option value="Feldolgozva">Feldolgozva</option>
                        <option value="Módosítás szükséges">Módosítás szükséges</option>
                        <option value="Új jelentkezés">Új jelentkezés</option>
                      </select>
                    </div>

                    <div className="sm:col-span-2 pt-4 border-t">
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Aktuális projekt:</span>
                          <div className="mt-1">
                            {customer.project?.name || 'Nincs kiválasztva'}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">Projekt ID:</span>
                          <div className="mt-1">
                            {customer.project?.id || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Cím</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Irányítószám</label>
                      <input
                        type="text"
                        value={customer.address.zipCode}
                        onChange={(e) => handleChange('address', 'zipCode', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Város</label>
                      <input
                        type="text"
                        value={customer.address.city}
                        onChange={(e) => handleChange('address', 'city', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Cím</label>
                      <input
                        type="text"
                        value={customer.address.fullAddress}
                        onChange={(e) => handleChange('address', 'fullAddress', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ország</label>
                      <input
                        type="text"
                        value={customer.address.country}
                        onChange={(e) => handleChange('address', 'country', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary Contact */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Elsődleges kapcsolattartó</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Vezetéknév</label>
                      <input
                        type="text"
                        value={customer.primaryContact.lastName}
                        onChange={(e) => handleChange('primaryContact', 'lastName', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Keresztnév</label>
                      <input
                        type="text"
                        value={customer.primaryContact.firstName}
                        onChange={(e) => handleChange('primaryContact', 'firstName', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={customer.primaryContact.email}
                        onChange={(e) => handleChange('primaryContact', 'email', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Telefon</label>
                      <input
                        type="text"
                        value={customer.primaryContact.phone}
                        onChange={(e) => handleChange('primaryContact', 'phone', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Tax Details */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Adózási információk</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Adószám</label>
                      <input
                        type="text"
                        value={customer.taxDetails.taxNumber}
                        onChange={(e) => handleChange('taxDetails', 'taxNumber', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">EU adószám</label>
                      <input
                        type="text"
                        value={customer.taxDetails.euTaxNumber}
                        onChange={(e) => handleChange('taxDetails', 'euTaxNumber', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Fizetési információk</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fizetési mód</label>
                      <select
                        value={customer.payment.defaultPaymentMethod}
                        onChange={(e) => handleChange('payment', 'defaultPaymentMethod', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="átutalás">Átutalás</option>
                        <option value="készpénz">Készpénz</option>
                        <option value="bankkártya">Bankkártya</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fizetési határidő (nap)</label>
                      <input
                        type="number"
                        value={customer.payment.defaultDueDate}
                        onChange={(e) => handleChange('payment', 'defaultDueDate', parseInt(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Megjegyzések</h2>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Számlázási megjegyzés</label>
                      <textarea
                        value={customer.notes.invoiceNote}
                        onChange={(e) => handleChange('notes', 'invoiceNote', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Belső megjegyzés</label>
                      <textarea
                        value={customer.notes.internalNote}
                        onChange={(e) => handleChange('notes', 'internalNote', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

